import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePDF } from 'react-to-pdf';
import './App.css';
import { supabase } from './supabaseClient';
import GameStats from './components/GameStats';
import GameCreator from './components/GameCreator';
import PlayerRoster from './components/PlayerRoster';
import StatsSummary from './components/StatsSummary';
import Login from './components/Login';
import SeasonSelector from './components/SeasonSelector';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Card, CardContent } from './components/ui/card';
import { Sun, Moon, FileDown, LogOut, Trophy, Users, ChevronDown, ChevronRight, BarChart3 } from 'lucide-react';

function App() {
  const [session, setSession] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [games, setGames] = useState([]);
  const [players, setPlayers] = useState([]);
  const [activeGameId, setActiveGameId] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('soccer_stats_theme');
    if (savedTheme !== null) {
      return savedTheme === 'dark';
    }
    return true;
  });
  const { toPDF, targetRef } = usePDF({ filename: 'soccer_stats.pdf' });
  const [isStatsSummaryOpen, setIsStatsSummaryOpen] = useState(true);
  const [isRosterOpen, setIsRosterOpen] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSession(session);
    }).catch(err => console.warn('Supabase auth session check warning:', err));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) setSession(session);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handleDemoLogin = () => {
    const demoSession = {
      user: {
        id: 'demo-user-12345',
        email: 'local-demo@soccerstats.app'
      },
      isDemo: true
    };
    setSession(demoSession);
  };

  // Fetch all seasons for user
  const fetchSeasons = useCallback(async () => {
    if (!session) return;
    try {
      const { data, error } = await supabase
        .from('seasons')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.warn('Seasons table query notice:', error.message);
      } else if (data && data.length > 0) {
        setSeasons(data);
        setSelectedSeasonId(prev => (prev === null ? data[data.length - 1].id : prev));
      }
    } catch (err) {
      console.warn('Seasons error:', err);
    }
  }, [session]);

  const fetchGames = useCallback(async () => {
    if (!session) return;
    try {
      let query = supabase
        .from('games')
        .select(`
          *,
          playerStats:player_stats(
            *,
            player:players(*)
          )
        `)
        .eq('user_id', session.user.id);

      if (selectedSeasonId) {
        query = query.eq('season_id', selectedSeasonId);
      }

      const { data, error } = await query;
      
      if (error) {
        console.warn('Games query notice:', error.message);
      } else if (data) {
        setGames(data);
      }
    } catch (err) {
      console.warn('Games fetch error:', err);
    }
  }, [session, selectedSeasonId]);

  const fetchPlayers = useCallback(async () => {
    if (!session) return;
    try {
      let query = supabase
        .from('players')
        .select('*')
        .eq('user_id', session.user.id);

      if (selectedSeasonId) {
        query = query.eq('season_id', selectedSeasonId);
      }

      const { data, error } = await query;
      
      if (error) {
        console.warn('Players query notice:', error.message);
      } else if (data) {
        setPlayers(data);
      }
    } catch (err) {
      console.warn('Players fetch error:', err);
    }
  }, [session, selectedSeasonId]);

  useEffect(() => {
    if (session) {
      fetchSeasons();
    }
  }, [session, fetchSeasons]);

  useEffect(() => {
    if (session) {
      fetchGames();
      fetchPlayers();
    }
  }, [session, selectedSeasonId, fetchGames, fetchPlayers]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('soccer_stats_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('soccer_stats_theme', 'light');
    }
  }, [darkMode]);

  // Compute active players and games filtered by selectedSeasonId
  const activePlayers = useMemo(() => {
    if (!selectedSeasonId) return players;
    return players.filter(p => !p.season_id || p.season_id === selectedSeasonId);
  }, [players, selectedSeasonId]);

  const activeGames = useMemo(() => {
    if (!selectedSeasonId) return games;
    return games.filter(g => !g.season_id || g.season_id === selectedSeasonId);
  }, [games, selectedSeasonId]);

  // Season Handlers
  const addSeason = async (seasonName) => {
    let newSeason = null;
    try {
      const { data, error } = await supabase
        .from('seasons')
        .insert({ name: seasonName, user_id: session.user.id })
        .select()
        .single();

      if (!error && data) {
        newSeason = data;
      }
    } catch (err) {
      console.warn('Supabase season insert error, using local state:', err);
    }

    if (!newSeason) {
      newSeason = {
        id: 'season-' + Date.now(),
        name: seasonName,
        user_id: session.user.id,
        created_at: new Date().toISOString()
      };
    }

    setSeasons(prev => [...prev, newSeason]);
    setSelectedSeasonId(newSeason.id);
  };

  const renameSeason = async (seasonId, newName) => {
    try {
      await supabase
        .from('seasons')
        .update({ name: newName })
        .eq('id', seasonId);
    } catch (err) {
      console.warn('Supabase season rename error:', err);
    }

    setSeasons(prev => prev.map(s => s.id === seasonId ? { ...s, name: newName } : s));
  };

  const deleteSeason = async (seasonId) => {
    try {
      await supabase
        .from('seasons')
        .delete()
        .eq('id', seasonId);
    } catch (err) {
      console.warn('Supabase season delete error:', err);
    }

    setSeasons(prev => prev.filter(s => s.id !== seasonId));
    setSelectedSeasonId(null);
  };

  // Game Handlers
  const addGame = async (gameName, location = null) => {
    const gamePayload = { name: gameName, user_id: session.user.id };
    if (selectedSeasonId) {
      gamePayload.season_id = selectedSeasonId;
    }
    if (location) {
      gamePayload.location = location;
    }

    let createdGame = null;

    try {
      const { data: newGame, error: gameError } = await supabase
        .from('games')
        .insert(gamePayload)
        .select()
        .single();

      if (!gameError && newGame) {
        const playerStats = activePlayers.map(player => ({
          game_id: newGame.id,
          player_id: player.id,
          goals: 0,
          assists: 0
        }));

        if (playerStats.length > 0) {
          await supabase.from('player_stats').insert(playerStats);
        }

        const { data: gameWithStats } = await supabase
          .from('games')
          .select(`
            *,
            playerStats:player_stats(
              *,
              player:players(*)
            )
          `)
          .eq('id', newGame.id)
          .single();

        if (gameWithStats) {
          createdGame = gameWithStats;
        }
      }
    } catch (err) {
      console.warn('Supabase game insert warning, using local state:', err);
    }

    if (!createdGame) {
      const fallbackGameId = 'game-' + Date.now();
      createdGame = {
        id: fallbackGameId,
        name: gameName,
        location: location || null,
        user_id: session.user.id,
        season_id: selectedSeasonId,
        opponent_score: 0,
        playerStats: activePlayers.map(player => ({
          game_id: fallbackGameId,
          player_id: player.id,
          goals: 0,
          assists: 0,
          player: player
        }))
      };
    }

    setGames(prevGames => [...prevGames, createdGame]);
  };

  const updateGameStats = async (gameId, playerId, statName, value) => {
    if (statName === 'opponent_score') {
      try {
        await supabase
          .from('games')
          .update({ opponent_score: value })
          .eq('id', gameId);
      } catch (err) {
        console.warn('Supabase update opponent_score error:', err);
      }

      setGames(prevGames => prevGames.map(game => game.id === gameId ? { ...game, opponent_score: value } : game));
      return;
    }

    try {
      await supabase
        .from('player_stats')
        .update({ [statName]: value })
        .eq('game_id', gameId)
        .eq('player_id', playerId);
    } catch (err) {
      console.warn('Supabase update player_stats error:', err);
    }

    setGames(prevGames => prevGames.map(game => {
      if (game.id !== gameId) return game;
      return {
        ...game,
        playerStats: (game.playerStats || []).map(ps => ps.player_id === playerId ? { ...ps, [statName]: value } : ps)
      };
    }));
  };

  const updateGameNotes = async (gameId, notes) => {
    try {
      await supabase
        .from('games')
        .update({ notes })
        .eq('id', gameId);
    } catch (err) {
      console.warn('Supabase update notes error:', err);
    }

    setGames(prevGames => prevGames.map(game => game.id === gameId ? { ...game, notes } : game));
  };

  const deleteGame = async (gameId) => {
    try {
      await supabase.from('player_stats').delete().eq('game_id', gameId);
      await supabase.from('games').delete().eq('id', gameId);
    } catch (err) {
      console.warn('Supabase delete game error:', err);
    }

    setGames(prev => prev.filter(g => g.id !== gameId));
    if (activeGameId === gameId) {
      setActiveGameId(null);
    }
  };

  // Player Handlers
  const addPlayer = async (playerName) => {
    const playerPayload = { name: playerName, user_id: session.user.id };
    if (selectedSeasonId) {
      playerPayload.season_id = selectedSeasonId;
    }

    let createdPlayer = null;

    try {
      const { data: newPlayer, error } = await supabase
        .from('players')
        .insert(playerPayload)
        .select()
        .single();

      if (!error && newPlayer) {
        createdPlayer = newPlayer;
        if (activeGames.length > 0) {
          const playerStats = activeGames.map(game => ({
            game_id: game.id,
            player_id: newPlayer.id,
            goals: 0,
            assists: 0
          }));
          await supabase.from('player_stats').insert(playerStats);
        }
      }
    } catch (err) {
      console.warn('Supabase add player error, using local state:', err);
    }

    if (!createdPlayer) {
      createdPlayer = {
        id: 'player-' + Date.now(),
        name: playerName,
        user_id: session.user.id,
        season_id: selectedSeasonId
      };
    }

    setPlayers(prevPlayers => [...prevPlayers, createdPlayer]);

    setGames(prevGames => prevGames.map(game => {
      if (selectedSeasonId && game.season_id && game.season_id !== selectedSeasonId) return game;
      return {
        ...game,
        playerStats: [
          ...(game.playerStats || []),
          {
            game_id: game.id,
            player_id: createdPlayer.id,
            goals: 0,
            assists: 0,
            player: createdPlayer
          }
        ]
      };
    }));
  };

  const deletePlayer = async (playerId) => {
    try {
      await supabase.from('players').delete().eq('id', playerId);
      await supabase.from('player_stats').delete().eq('player_id', playerId);
    } catch (err) {
      console.warn('Supabase delete player error:', err);
    }

    setPlayers(prevPlayers => prevPlayers.filter(player => player.id !== playerId));
  };

  const reorderPlayers = (newPlayersOrder) => {
    setPlayers(newPlayersOrder);
  };

  const updatePlayerName = async (playerId, newName) => {
    const trimmed = newName.trim();
    if (!trimmed) return;

    try {
      await supabase
        .from('players')
        .update({ name: trimmed })
        .eq('id', playerId);
    } catch (err) {
      console.warn('Supabase update player error:', err);
    }

    setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, name: trimmed } : p));

    setGames(prevGames => prevGames.map(game => ({
      ...game,
      playerStats: (game.playerStats || []).map(ps => ps.player_id === playerId ? { ...ps, player: { ...ps.player, name: trimmed } } : ps)
    })));
  };

  const importRosterFromSeason = async (sourceSeasonId) => {
    if (!selectedSeasonId) {
      alert("Please select an active season first to import players into.");
      return;
    }
    if (sourceSeasonId === selectedSeasonId) return;

    try {
      let sourcePlayers = [];
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('season_id', sourceSeasonId);

      if (!error && data && data.length > 0) {
        sourcePlayers = data;
      } else {
        sourcePlayers = players.filter(p => p.season_id === sourceSeasonId);
      }

      if (sourcePlayers.length === 0) {
        alert("No players found in the selected source season.");
        return;
      }

      const existingNames = new Set(activePlayers.map(p => p.name.trim().toLowerCase()));
      const playersToImport = sourcePlayers.filter(
        p => !existingNames.has(p.name.trim().toLowerCase())
      );

      if (playersToImport.length === 0) {
        alert("All players from the selected season are already in this roster!");
        return;
      }

      const importedPlayers = [];

      for (const p of playersToImport) {
        const playerPayload = {
          name: p.name,
          user_id: session.user.id,
          season_id: selectedSeasonId
        };

        let newPlayer = null;
        try {
          const { data: inserted, error: insertErr } = await supabase
            .from('players')
            .insert(playerPayload)
            .select()
            .single();

          if (!insertErr && inserted) {
            newPlayer = inserted;
          }
        } catch (err) {
          console.warn('Import player Supabase error:', err);
        }

        if (!newPlayer) {
          newPlayer = {
            id: 'player-' + Date.now() + '-' + Math.random().toString(36).substr(2, 4),
            name: p.name,
            user_id: session.user.id,
            season_id: selectedSeasonId
          };
        }

        importedPlayers.push(newPlayer);
      }

      if (activeGames.length > 0 && importedPlayers.length > 0) {
        const newStats = [];
        activeGames.forEach(game => {
          importedPlayers.forEach(player => {
            newStats.push({
              game_id: game.id,
              player_id: player.id,
              goals: 0,
              assists: 0
            });
          });
        });

        try {
          await supabase.from('player_stats').insert(newStats);
        } catch (err) {
          console.warn('Import player_stats error:', err);
        }
      }

      setPlayers(prev => [...prev, ...importedPlayers]);

      setGames(prevGames =>
        prevGames.map(game => {
          if (game.season_id && game.season_id !== selectedSeasonId) return game;
          return {
            ...game,
            playerStats: [
              ...(game.playerStats || []),
              ...importedPlayers.map(p => ({
                game_id: game.id,
                player_id: p.id,
                goals: 0,
                assists: 0,
                player: p
              }))
            ]
          };
        })
      );

      const sourceSeasonObj = seasons.find(s => s.id === sourceSeasonId);
      const sourceName = sourceSeasonObj ? sourceSeasonObj.name : 'selected season';
      alert(`Successfully imported ${importedPlayers.length} player(s) from "${sourceName}"!`);
    } catch (err) {
      console.error('Failed to import roster:', err);
      alert('An error occurred while importing roster.');
    }
  };

  const handleLogout = async () => {
    setSession(null);
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('Logout warning:', err);
    }
  };

  const activeSeason = seasons.find(s => s.id === selectedSeasonId);
  const activeSeasonName = activeSeason ? activeSeason.name : (selectedSeasonId ? 'Selected Season' : 'All Seasons');

  if (!session) {
    return <Login onDemoLogin={handleDemoLogin} />;
  }

  // --- GAME DETAIL PAGE VIEW ---
  if (activeGameId) {
    const activeGame = games.find(g => g.id === activeGameId);
    if (activeGame) {
      return (
        <div ref={targetRef} className="min-h-screen bg-background text-foreground px-2 py-4 sm:p-8 transition-colors duration-200 overflow-x-hidden">
          <GameStats
            game={activeGame}
            onUpdateStats={updateGameStats}
            onUpdateNotes={updateGameNotes}
            onDeleteGame={deleteGame}
            players={activePlayers}
            onBack={() => setActiveGameId(null)}
          />
        </div>
      );
    }
  }

  // --- DASHBOARD VIEW ---
  return (
    <div ref={targetRef} className="min-h-screen bg-background text-foreground px-2 py-4 sm:p-8 pb-16 transition-colors duration-200">
      <div className="max-w-full sm:max-w-4xl mx-auto space-y-6">
        
        {/* Header Bar */}
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Soccer Stat Tracker</h1>
                {session?.isDemo && (
                  <Badge variant="success" className="text-xs">
                    Local Demo Mode
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground font-medium">
                Manage rosters, seasons, and match statistics
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 self-stretch sm:self-auto justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDarkMode(!darkMode)}
              className="flex items-center gap-1.5"
            >
              {darkMode ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-indigo-500" />}
              <span>{darkMode ? 'Light' : 'Dark'}</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => toPDF()}
              className="flex items-center gap-1.5"
            >
              <FileDown className="w-4 h-4 text-emerald-500" />
              <span>Export PDF</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-destructive hover:bg-destructive/10"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </header>

        {/* 1. Season Selector Toolbar */}
        <SeasonSelector
          seasons={seasons}
          selectedSeasonId={selectedSeasonId}
          onSelectSeason={setSelectedSeasonId}
          onAddSeason={addSeason}
          onRenameSeason={renameSeason}
          onDeleteSeason={deleteSeason}
        />

        {/* 2. Matches & Games List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-lg font-bold tracking-tight">Matches & Games</h2>
            <Badge variant="outline">{activeGames.length} Games</Badge>
          </div>

          {activeGames.length === 0 ? (
            <Card className="p-8 text-center border-dashed">
              <p className="text-muted-foreground text-sm">
                No games created for <span className="font-semibold">{activeSeasonName}</span> yet. Use the form below to add a match!
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {activeGames.map(game => {
                const totalGoals = (game.playerStats || []).reduce((acc, ps) => acc + (ps.goals || 0), 0);
                const opponentScore = game.opponent_score || 0;
                const outcome = totalGoals > opponentScore ? 'WIN' : (totalGoals < opponentScore ? 'LOSS' : 'DRAW');

                return (
                  <Card
                    key={game.id}
                    onClick={() => setActiveGameId(game.id)}
                    className="border shadow-sm hover:border-primary hover:shadow-md transition-all cursor-pointer p-4 flex items-center justify-between group"
                  >
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="font-semibold text-base sm:text-lg group-hover:text-primary transition-colors">
                        {game.name}
                      </span>
                      {game.location && (
                        <Badge variant="outline" className="text-xs font-normal">
                          {game.location}
                        </Badge>
                      )}
                      <Badge variant={outcome === 'WIN' ? 'success' : (outcome === 'LOSS' ? 'destructive' : 'secondary')}>
                        {totalGoals} - {opponentScore} ({outcome})
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-primary font-medium shrink-0">
                      <span className="hidden sm:inline">Track Game</span>
                      <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* 3. Add a Game */}
        <GameCreator onAddGame={addGame} seasonName={activeSeasonName} />

        {/* 4. Player Stats & Roster Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-lg font-bold tracking-tight">Player Stats & Roster</h2>
            <Badge variant="outline">{activePlayers.length} {activePlayers.length === 1 ? 'Player' : 'Players'}</Badge>
          </div>

          {/* Player Stats Summary Accordion Card */}
          <Card className="border shadow-sm overflow-hidden">
            <button
              onClick={() => setIsStatsSummaryOpen(v => !v)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                <span className="font-semibold text-lg">Player Stats Summary</span>
                <Badge variant="secondary" className="font-normal text-xs ml-1">
                  {activeSeasonName}
                </Badge>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isStatsSummaryOpen ? 'rotate-180' : ''}`} />
            </button>
            {isStatsSummaryOpen && (
              <CardContent className="pt-2">
                <StatsSummary players={activePlayers} games={activeGames} seasonName={activeSeasonName} />
              </CardContent>
            )}
          </Card>

          {/* Player Roster Accordion Card */}
          <Card className="border shadow-sm overflow-hidden">
            <button
              onClick={() => setIsRosterOpen(v => !v)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <span className="font-semibold text-lg">Player Roster</span>
                <Badge variant="secondary" className="font-normal text-xs ml-1">
                  {activeSeasonName}
                </Badge>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isRosterOpen ? 'rotate-180' : ''}`} />
            </button>
            {isRosterOpen && (
              <CardContent className="pt-2">
                <PlayerRoster 
                  players={activePlayers} 
                  onAddPlayer={addPlayer} 
                  onDeletePlayer={deletePlayer} 
                  onReorderPlayers={reorderPlayers} 
                  onUpdatePlayerName={updatePlayerName}
                  seasonName={activeSeasonName}
                  seasons={seasons}
                  selectedSeasonId={selectedSeasonId}
                  onImportRoster={importRosterFromSeason}
                />
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default App;
